import React from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { ClassFormValues } from '../ClassForm';
import { format, parse } from 'date-fns';
import { parseDateOnlyString } from '@/utils/dateUtils';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import * as Dialog from '@radix-ui/react-dialog';
import * as Checkbox from '@radix-ui/react-checkbox';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { RRule } from 'rrule';
import { useFormContext } from 'react-hook-form';

const RECURRENCE_TYPES = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const WEEKDAYS = [
  { value: 1, label: 'Monday', rrule: RRule.MO },
  { value: 2, label: 'Tuesday', rrule: RRule.TU },
  { value: 3, label: 'Wednesday', rrule: RRule.WE },
  { value: 4, label: 'Thursday', rrule: RRule.TH },
  { value: 5, label: 'Friday', rrule: RRule.FR },
  { value: 6, label: 'Saturday', rrule: RRule.SA },
  { value: 0, label: 'Sunday', rrule: RRule.SU },
];

interface ScheduleFieldsProps {
  form: UseFormReturn<ClassFormValues>;
}

export const ScheduleFields: React.FC<ScheduleFieldsProps> = ({ form }) => {
  const { control, register, setValue, watch } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'schedules',
  });
  const schedules = watch('schedules') || [];
  const scheduleType = watch('schedule_type') || 'recurring_pattern';
  
  // Ensure at least one row exists
  React.useEffect(() => {
    if (schedules.length === 0) {
      append({ weekday: undefined, start_time: '', end_time: '', date: '' });
    }
    // Only run when schedules.length changes
    // eslint-disable-next-line
  }, [schedules.length]);

  // Recurrence state for the whole class
  const recurrence = form.watch('recurrence') || {
    type: 'weekly',
    interval: 1,
    end: 'never',
    count: 1,
    until: '',
  };

  // Helper to format time in 12-hour format with AM/PM
  const formatTime12Hour = (timeString?: string) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  // Human-readable preview
  const preview = schedules
    .map(s => {
      if (scheduleType === 'specific_dates' && s.date) {
        const date = parseDateOnlyString(s.date).toLocaleDateString('en-US', { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric' 
        });
        return s.start_time && s.end_time
          ? `${date} ${formatTime12Hour(s.start_time)}–${formatTime12Hour(s.end_time)}`
          : null;
      } else {
        const day = WEEKDAYS.find(w => w.value === Number(s.weekday))?.label;
        return day && s.start_time && s.end_time
          ? `${day} ${formatTime12Hour(s.start_time)}–${formatTime12Hour(s.end_time)}`
          : null;
      }
    })
    .filter(Boolean)
    .join(', ');

  // Validation logic
  const [validationError, setValidationError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let error: string | null = null;
    const seen: Record<string, { start: number; end: number }[]> = {};
    for (let i = 0; i < schedules.length; i++) {
      const { weekday, start_time, end_time } = schedules[i] || {};
      if (weekday === undefined) continue;
      if (!start_time || !end_time) continue;
      const [sh, sm] = start_time.split(":").map(Number);
      const [eh, em] = end_time.split(":").map(Number);
      const start = sh * 60 + sm;
      const end = eh * 60 + em;
      if (end <= start) {
        error = `End time must be after start time for ${WEEKDAYS.find(w => w.value === Number(weekday))?.label}.`;
        break;
      }
      const key = weekday.toString();
      if (!seen[key]) seen[key] = [];
      for (const other of seen[key]) {
        if (start < other.end && end > other.start) {
          error = `Overlapping or duplicate times for ${WEEKDAYS.find(w => w.value === Number(weekday))?.label}.`;
          break;
        }
      }
      if (error) break;
      seen[key].push({ start, end });
    }
    setValidationError(error);
  }, [schedules]);

  // Helper to get recurrence summary
  const getRecurrenceSummary = () => {
    let summary = `Repeats every ${recurrence.interval} week(s)`;
    if (recurrence.end === 'count') summary += `, ${recurrence.count} times`;
    if (recurrence.end === 'until') summary += `, until ${recurrence.until}`;
    return summary;
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block font-medium text-sm mb-2">Schedule Type</label>
        <Select
          value={scheduleType}
          onValueChange={(value) => {
            setValue('schedule_type', value as any);
            // Clear existing schedules when switching types
            setValue('schedules', []);
            if (value === 'specific_dates') {
              append({ date: '', start_time: '', end_time: '' });
            } else {
              append({ weekday: 1, start_time: '', end_time: '' });
            }
          }}
        >
          <SelectTrigger className="w-full max-w-md">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="specific_dates">
              <div className="space-y-1">
                <div className="font-medium">Specific Dates</div>
                <div className="text-xs text-muted-foreground">One-time sessions, workshops, camps</div>
              </div>
            </SelectItem>
            <SelectItem value="recurring_pattern">
              <div className="space-y-1">
                <div className="font-medium">Recurring Pattern</div>
                <div className="text-xs text-muted-foreground">Weekly classes, ongoing programs</div>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block font-medium text-sm mb-2">
          {scheduleType === 'specific_dates' ? 'Session Dates' : 'Weekly Schedule'}
        </label>
        {validationError && (
          <div className="text-sm text-red-600 mb-2">{validationError}</div>
        )}
        <div className="space-y-2">
          {fields.map((field, idx) => (
            <div key={field.id} className="mb-4 border rounded p-3 bg-gray-50">
              <div className="flex items-center gap-2">
                {scheduleType === 'specific_dates' ? (
                  <>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-48 justify-start text-left font-normal",
                            !schedules[idx]?.date && "text-muted-foreground"
                          )}
                        >
                          {schedules[idx]?.date ? (
                            format(parseDateOnlyString(schedules[idx].date), "PPP")
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-50" align="start">
                        <Calendar
                          mode="single"
                          selected={schedules[idx]?.date ? parseDateOnlyString(schedules[idx].date) : undefined}
                          onSelect={(date) => setValue(`schedules.${idx}.date`, date ? format(date, 'yyyy-MM-dd') : '')}
                          disabled={(date) => date < new Date()}
                          initialFocus
                          className="p-3"
                        />
                      </PopoverContent>
                    </Popover>
                    <Input
                      type="time"
                      className="w-28"
                      {...register(`schedules.${idx}.start_time`)}
                    />
                    <span>–</span>
                    <Input
                      type="time"
                      className="w-28"
                      {...register(`schedules.${idx}.end_time`)}
                    />
                  </>
                ) : (
                  <>
                    <Select
                      value={schedules[idx]?.weekday?.toString() || ''}
                      onValueChange={val => setValue(`schedules.${idx}.weekday`, Number(val))}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue placeholder="Day" />
                      </SelectTrigger>
                      <SelectContent>
                        {WEEKDAYS.map(w => (
                          <SelectItem key={w.value} value={w.value.toString()}>{w.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="time"
                      className="w-28"
                      {...register(`schedules.${idx}.start_time`)}
                    />
                    <span>–</span>
                    <Input
                      type="time"
                      className="w-28"
                      {...register(`schedules.${idx}.end_time`)}
                    />
                  </>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(idx)}
                  disabled={fields.length === 1}
                  aria-label="Remove schedule row"
                >
                  <span aria-hidden>✕</span>
                </Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (scheduleType === 'specific_dates') {
                append({ date: '', start_time: '', end_time: '' });
              } else {
                append({ weekday: undefined, start_time: '', end_time: '' });
              }
            }}
            disabled={!!validationError}
          >
            {scheduleType === 'specific_dates' ? '+ Add another session date' : '+ Add another weekly time slot'}
          </Button>
        </div>
      </div>
      {/* Recurrence section - only for recurring patterns */}
      {scheduleType === 'recurring_pattern' && (
        <div className="mt-4 p-4 border rounded bg-gray-50">
          <label className="block font-medium text-sm mb-1">Recurrence</label>
          <div className="flex gap-4 items-center mb-2">
            <span>Every</span>
            <input
              type="number"
              min={1}
              className="w-16 border rounded px-2 py-1"
              value={recurrence.interval}
              onChange={e => form.setValue('recurrence.interval', Number(e.target.value))}
              aria-label="Recurrence interval in weeks"
            />
            <span>week(s)</span>
          </div>
          <div className="flex gap-4 items-center mb-2">
            <span>Ends</span>
            <select
              className="border rounded px-2 py-1"
              value={recurrence.end}
              onChange={e => form.setValue('recurrence.end', e.target.value)}
            >
              <option value="never">Never</option>
              <option value="count">After</option>
              <option value="until">On date</option>
            </select>
            {recurrence.end === 'count' && (
              <input
                type="number"
                min={1}
                className="w-16 border rounded px-2 py-1"
                value={recurrence.count}
                onChange={e => form.setValue('recurrence.count', Number(e.target.value))}
                aria-label="Number of recurrences"
              />
            )}
            {recurrence.end === 'until' && (
              <input
                type="date"
                className="border rounded px-2 py-1"
                value={recurrence.until}
                onChange={e => form.setValue('recurrence.until', e.target.value)}
              />
            )}
          </div>
          <div className="text-sm text-gray-700 font-medium mt-2">{getRecurrenceSummary()}</div>
        </div>
      )}
      {preview && (
        <div className="text-base text-gray-800 font-semibold mt-2">
          <strong>Preview:</strong> {scheduleType === 'specific_dates' 
            ? `${schedules.length} session${schedules.length > 1 ? 's' : ''}: ${preview}`
            : `${preview} ${scheduleType === 'recurring_pattern' ? '(recurring weekly)' : ''}`
          }
        </div>
      )}
    </div>
  );
}; 